import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  InlineError,
  Input,
  Select,
  Textarea,
} from '../../components/ui';
import { useCopySets } from './hooks';
import type { LeadActivity } from './leadActivity';
import { canRemoveEntry } from './leadActivity';
import { useAuth } from '../auth/AuthProvider';
import { copyMessageLabel, DRAFT_COPY_GROUP } from './copyMessages';
import type { CopySet } from './types';
import { messageCommand, messageSelectionError, useSaveMessageSelection } from './messageSelection';
import type { MessageCommand, MessageSubject, MessageTarget } from './messageSelection';
import { activityCorrectionCommand, useSaveActivityCorrection } from './activityCorrection';
import type { ActivityCorrectionCommand } from './activityCorrection';

/** The displayed copy object, not a background-refreshed catalog row, owns the
 * preview revision. Retrying keeps the whole request and UUID unchanged. */
export function MessageSelectionDialog({
  subject,
  activity,
  detailActivity,
  onClose,
  onSaved,
}: {
  subject: MessageSubject;
  activity: Pick<
    LeadActivity,
    'id' | 'copy_set_id' | 'message_slot' | 'message_selected_at' | 'message_version'
  >;
  /** Full displayed row for an optional admin correction. The database
   * independently enforces that role and compares every expected field. */
  detailActivity?: LeadActivity;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const catalog = useCopySets();
  const { access } = useAuth();
  const save = useSaveMessageSelection();
  const correct = useSaveActivityCorrection();
  const [displayedActivity] = useState(activity);
  const [detailSnapshot] = useState(detailActivity);
  const mayCorrect =
    detailSnapshot !== undefined &&
    canRemoveEntry(access?.status === 'approved' ? access.profile : null, detailSnapshot);
  const [mode, setMode] = useState<'library' | 'draft' | 'clear' | 'keep'>('library');
  const [preview, setPreview] = useState<CopySet | null>(null);
  const [slot, setSlot] = useState(1);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState<MessageCommand | null>(null);
  const [retryCorrection, setRetryCorrection] = useState<ActivityCorrectionCommand | null>(null);
  const [correctDetails, setCorrectDetails] = useState(false);
  const [notes, setNotes] = useState(detailSnapshot?.body ?? '');
  const originalDay = detailSnapshot?.occurred_at.slice(0, 10) ?? '';
  const [day, setDay] = useState(originalDay);
  const copies = (catalog.data ?? []).filter(
    (copy) => copy.channel === 'linkedin' && copy.archived_at === null,
  );
  const slots =
    preview === null
      ? []
      : Array.from({ length: 8 }, (_, i) => i + 1).filter((n) =>
          String(preview[`e${n}_body` as keyof CopySet] ?? '').trim(),
        );
  const pending = save.isPending || correct.isPending;
  const frozen = pending || retry !== null || retryCorrection !== null;

  function submit(command?: MessageCommand) {
    try {
      // A permission refresh must never turn a lost-response correction into
      // a new selection/clear request. Replay the frozen command first and
      // let the database decide whether this actor can still perform it.
      if (retryCorrection !== null || correctDetails) {
        if (detailSnapshot === undefined || (!mayCorrect && retryCorrection === null))
          throw new Error('You can no longer correct this entry. Close and reload it.');
        const next =
          retryCorrection ??
          activityCorrectionCommand(
            subject,
            detailSnapshot,
            notes,
            day === originalDay ? undefined : day,
            reason,
            crypto.randomUUID(),
            mode === 'keep'
              ? undefined
              : messageCommand(
                  subject,
                  displayedActivity,
                  chosenTarget(),
                  reason,
                  crypto.randomUUID(),
                ),
          );
        setError('');
        setRetryCorrection(next);
        correct.mutate(next, {
          onSuccess: () => {
            onSaved?.();
            onClose();
          },
          onError: (cause) => setError(messageSelectionError(cause.message)),
        });
        return;
      }
      const next =
        command ??
        messageCommand(subject, displayedActivity, chosenTarget(), reason, crypto.randomUUID());
      setError('');
      setRetry(next);
      save.mutate(next, {
        onSuccess: () => {
          onSaved?.();
          onClose();
        },
        onError: (cause) => setError(messageSelectionError(cause.message)),
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Review this selection.');
    }
  }

  function chosenTarget(): MessageTarget {
    const target: MessageTarget =
      mode === 'library'
        ? preview === null
          ? (() => {
              throw new Error('Choose and preview a message first.');
            })()
          : { kind: 'library', copy: preview, slot }
        : mode === 'draft'
          ? { kind: 'draft', name, body }
          : { kind: 'clear' };
    return target;
  }

  return (
    <Dialog
      open
      title="Message used"
      onClose={() => {
        if (!pending) onClose();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Close
          </Button>
          <Button disabled={pending} onClick={() => submit(retry ?? undefined)}>
            {retryCorrection !== null
              ? 'Retry same correction'
              : correctDetails
                ? 'Save correction'
                : retry === null
                  ? 'Save selection'
                  : 'Retry same selection'}
          </Button>
        </>
      }
    >
      <p>
        Selecting a message records your choice. It does not send a DM or change library wording.
      </p>
      {error !== '' && <InlineError>{error}</InlineError>}
      {mayCorrect && (
        <Checkbox
          label="Correct this entry's notes or date too"
          checked={correctDetails}
          disabled={frozen}
          onChange={(e) => {
            setCorrectDetails(e.target.checked);
            setMode(e.target.checked ? 'keep' : 'library');
          }}
        />
      )}
      {correctDetails && (
        <>
          <Textarea
            label="Entry notes"
            value={notes}
            disabled={frozen}
            onChange={(e) => setNotes(e.target.value)}
          />
          <Input
            label="Entry date"
            type="date"
            value={day}
            disabled={frozen}
            onChange={(e) => setDay(e.target.value)}
          />
          <p>
            Admin-only. Notes, date and any message change save together. Earlier values stay in
            history.
          </p>
        </>
      )}
      {catalog.isError && (
        <InlineError>
          Messages could not be loaded. Close and reload before choosing one.
        </InlineError>
      )}
      <Select
        label="Record from"
        value={mode}
        disabled={frozen}
        options={[
          ...(correctDetails
            ? [{ value: 'keep', label: 'Keep the recorded message unchanged' }]
            : []),
          { value: 'library', label: 'Copy Library' },
          { value: 'draft', label: 'Paste a one-off (Draft)' },
          { value: 'clear', label: 'Message not recorded' },
        ]}
        onChange={(e) => setMode(e.target.value as typeof mode)}
      />
      {mode === 'library' && (
        <>
          <Select
            label="Message used"
            value={preview?.id ?? ''}
            disabled={frozen}
            options={[
              { value: '', label: 'Choose a message' },
              ...copies
                .filter((c) => c.review_state !== 'draft')
                .map((c) => ({ value: c.id, label: copyMessageLabel(c) })),
            ]}
            groups={[
              {
                label: DRAFT_COPY_GROUP,
                options: copies
                  .filter((c) => c.review_state === 'draft')
                  .map((c) => ({ value: c.id, label: copyMessageLabel(c) })),
              },
            ]}
            onChange={(e) => {
              const copy = copies.find((c) => c.id === e.target.value) ?? null;
              setPreview(copy);
              setSlot(
                copy === null
                  ? 1
                  : (Array.from({ length: 8 }, (_, i) => i + 1).find((n) =>
                      String(copy[`e${n}_body` as keyof CopySet] ?? '').trim(),
                    ) ?? 1),
              );
            }}
          />
          <Select
            label="Message slot"
            value={String(slot)}
            disabled={frozen || preview === null}
            options={slots.map((n) => ({ value: String(n), label: `Slot ${n}` }))}
            onChange={(e) => setSlot(Number(e.target.value))}
          />
          {preview !== null && (
            <p
              className="tracker-field-note"
              style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
            >
              {String(preview[`e${slot}_body` as keyof CopySet] ?? '')}
            </p>
          )}
        </>
      )}
      {mode === 'draft' && (
        <>
          <p>Shared with the team and Alex as Draft. Using or editing it does not approve it.</p>
          <Input
            label="Draft name"
            value={name}
            disabled={frozen}
            onChange={(e) => setName(e.target.value)}
          />
          <Textarea
            label="Message actually used"
            value={body}
            disabled={frozen}
            onChange={(e) => setBody(e.target.value)}
          />
        </>
      )}
      <Textarea
        label={
          correctDetails || activity.message_selected_at !== null
            ? 'Correction reason'
            : 'Reason (optional)'
        }
        value={reason}
        disabled={frozen}
        onChange={(e) => setReason(e.target.value)}
      />
      {(retry !== null || retryCorrection !== null) && (
        <p>
          Retry keeps the original selection and request. Close and reload to make a different
          choice.
        </p>
      )}
    </Dialog>
  );
}
